import type { GroupedOption } from '@ksyos/design-system';
import { DateTime } from '@ksyos/date-time';
import { groupBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import type { IPossibleActivityPerformer } from '@common/interfaces/serviceWorkflow';
import type { IRecipient } from '@common/recipient';
import { getDisplayName } from '@common/utils/getDisplayName';
import { FormParagraphFormGroup } from '@frontend/components/fields/FormParagraphField';
import { listActivityPerformers } from '@frontend/services/api/activityPerformers';
import { getSelectedLanguage } from '@frontend/translate';
import { useProcessActivityEditContext } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { SelectFormGroup } from '../processActivityFormGroupFields';

type SelectProcessActivityPerformerProps = Readonly<{
    recipientActivityIds: string[];
    serviceId: string;
}>;

export function SelectProcessActivityPerformerOrOrganization(props: SelectProcessActivityPerformerProps) {
    const abortSignal = useAbortSignal();
    const [activityPerformers, setActivityPerformers] = useState<IPossibleActivityPerformer[]>([]);
    const { data, onChange } = useProcessActivityEditContext<{ recipient: IRecipient }>();
    const translate = getSelectedLanguage();

    const selectOptions = useMemo(() => {
        const options: GroupedOption[] = [];

        const groupedActivityPerformers = groupBy(activityPerformers, 'organization.id');
        Object.entries(groupedActivityPerformers).forEach((keyValuePair) => {
            const organizationId = keyValuePair[0];
            const activityPerformersGroup = keyValuePair[1];
            const organization = activityPerformersGroup[0].organization;
            const organizationIsDisabled = !organization.isRequestRecipient;
            const organizationCityLabel = organization.city ? `, ${organization.city}` : '';

            // Group header
            options.push({
                isDisabled: organizationIsDisabled,
                isGroupHeader: true,
                label: `${organization.name}${organizationCityLabel}`,
                value: organizationId,
            });

            // Group options
            activityPerformersGroup.forEach((activityPerformer) => {
                const practitioner = activityPerformer.practitioner;
                const absenceEndDate = practitioner.absenceEndDate;
                let label = getDisplayName(practitioner);

                if (absenceEndDate) {
                    label += ` (${translate.request.absentUpToAndIncluding} ${DateTime.from(absenceEndDate).format(
                        'DD-MM-YYYY'
                    )})`;
                } else if (!practitioner.isRequestRecipient) {
                    label += ` (${translate.request.selectableOnOrganization})`;
                }

                options.push({
                    isDisabled: !practitioner.isRequestRecipient || !!absenceEndDate,
                    isGroupOption: true,
                    label: label,
                    value: `${activityPerformer.organization.id}_${practitioner.id}`,
                });
            });
        });

        return options;
    }, [activityPerformers]);
    const nonDisabledOptions = selectOptions.filter((option) => !option.isDisabled);

    useEffect(() => {
        loadActivityPerformers(props.serviceId);
    }, [props.serviceId]);

    useEffect(() => {
        if (nonDisabledOptions.length === 1) {
            handleChange(nonDisabledOptions[0].value);
        }
    }, [activityPerformers]);

    if (nonDisabledOptions.length === 1) {
        return (
            <FormParagraphFormGroup label={getSelectedLanguage().common.sendTo}>
                {nonDisabledOptions[0].label}
            </FormParagraphFormGroup>
        );
    } else {
        const organizationValue = data.recipient.organizationId;
        const practitionerValue = data.recipient.practitionerId ? `_${data.recipient.practitionerId}` : '';
        const value = `${organizationValue}${practitionerValue}`;

        return (
            <SelectFormGroup
                fieldKey="recipient.organizationId"
                isRequired={true}
                label={getSelectedLanguage().common.sendTo}
                onChange={(selectValue) => handleChange(selectValue)}
                options={selectOptions}
                placeholder={`${getSelectedLanguage().common.sendTo}...`}
                value={value}
            />
        );
    }

    function handleChange(value: string | null) {
        let recipient: IRecipient = { organizationId: null, practitionerId: null };
        if (value) {
            const valuesAsArray = value.split('_');
            recipient = { organizationId: valuesAsArray[0], practitionerId: valuesAsArray?.[1] ?? null };
        }
        onChange({ ...data, recipient }, ['recipient.organizationId', 'recipient.practitionerId']);
    }

    async function loadActivityPerformers(serviceId: string) {
        const queryParams = {
            includeAbsences: true,
            isRequestRecipient: true,
            serviceActivityIds: props.recipientActivityIds,
        };
        const nextActivityPerformers = (await listActivityPerformers(
            serviceId,
            queryParams,
            abortSignal
        )) as unknown as IPossibleActivityPerformer[];
        setActivityPerformers(nextActivityPerformers);
    }
}
